import { useState, useEffect } from "react";
import { FONT, C } from "../lib/programConstants";
import { clearAuth } from "../lib/tokenStore";
import ProgramSelectorStep from "./ProgramSelectorStep";
import ProgramDashboardStep from "./ProgramDashboardStep";
import WorkoutStepAuth from "./WorkoutStepAuth";
import { fetchProgramFull, fetchWorkoutPrograms } from "../services/WorkoutEndUserService";
import {
  transformToProgramDashboard,
} from "../lib/workoutProgramTransformer";

function ProgramStepWrapper({ programData, onWorkoutTap, onBack }) {
  const dashboardData = programData ? transformToProgramDashboard(programData) : null;

  const handleClick = (e) => {
    let el = e.target;

    while (el && el !== e.currentTarget) {
      if (el.classList && el.classList.contains("day-card-clickable")) {
        e.preventDefault();
        e.stopPropagation();

        const sessionId = el.getAttribute("data-session-id");
        const weekNum = el.getAttribute("data-week");
        const dayIndex = el.getAttribute("data-day-index");

        if (dashboardData) {
          let dayData = dashboardData.weeks
            .flatMap((w) => w.days)
            .find((d) => d.sessionId === sessionId || d.sessionId === parseInt(sessionId));

          if (!dayData && weekNum && dayIndex !== null) {
            const week = dashboardData.weeks.find((w) => w.week === parseInt(weekNum));
            if (week && week.days[parseInt(dayIndex)]) {
              dayData = week.days[parseInt(dayIndex)];
            }
          }

          if (dayData) {
            let actualSession = null;
            if (programData && programData.sessions) {
              actualSession = programData.sessions.find(
                (s) =>
                  s.session_id === sessionId ||
                  s.session_id === parseInt(sessionId) ||
                  s.id === sessionId ||
                  s.id === parseInt(sessionId) ||
                  s.session_id === dayData.sessionId ||
                  s.id === dayData.sessionId
              );
            }

            if (actualSession) {
              onWorkoutTap({ ...dayData, sessionData: actualSession });
            } else {
              const mockSession = {
                id: dayData.sessionId || sessionId,
                session_id: dayData.sessionId || sessionId,
                session_name: dayData.label,
                workout_type: dayData.focus,
                blocks: dayData.blocks || [],
                duration_minutes: dayData.estTime,
                notes: `${dayData.label} workout`,
                status: dayData.completed ? "completed" : "scheduled",
              };
              onWorkoutTap({ ...dayData, sessionData: mockSession });
            }
            return;
          }
        }
        return;
      }

      const text = el.textContent || "";
      if (text.match(/^Today:/) || (el.classList && el.classList.contains("today-workout-tap"))) {
        e.preventDefault();
        e.stopPropagation();

        if (dashboardData) {
          const todayDay = dashboardData.weeks.flatMap((w) => w.days).find((d) => d.isToday);

          if (todayDay) {
            let actualSession = null;
            if (programData && programData.sessions) {
              actualSession = programData.sessions.find(
                (s) => s.session_id === todayDay.sessionId || s.id === todayDay.sessionId
              );
            }

            if (actualSession) {
              onWorkoutTap({ ...todayDay, sessionData: actualSession });
            } else {
              const mockSession = {
                id: todayDay.sessionId,
                session_id: todayDay.sessionId,
                session_name: todayDay.label,
                workout_type: todayDay.focus,
                blocks: todayDay.blocks || [],
                duration_minutes: todayDay.estTime,
                notes: `${todayDay.label} workout`,
                status: todayDay.completed ? "completed" : "scheduled",
              };
              onWorkoutTap({ ...todayDay, sessionData: mockSession });
            }
          }
        }
        return;
      }

      el = el.parentElement;
    }
  };

  return (
    <div onClick={handleClick} style={{ cursor: "default" }}>
      <style>{`
        .today-workout-tap { cursor: pointer !important; transition: transform 0.1s ease; }
        .today-workout-tap:hover { transform: translateX(2px); }
        .day-card-clickable { cursor: pointer !important; transition: background 0.15s ease; }
        .day-card-clickable:hover { background: rgba(43,122,120,0.04) !important; }
      `}</style>
      <ProgramDashboardStep programData={dashboardData} onLogout={onBack} />
    </div>
  );
}

export default function ProgramJourneyAuth({ programs = [] }) {
  const [step, setStep] = useState("selector");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [programList, setProgramList] = useState(programs);
  const [programData, setProgramData] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  // If only one program, skip selector and go straight to dashboard
  useEffect(() => {
    if (programList.length === 1) {
      handleSelectProgram(programList[0]);
    }
  }, []);

  const handleSelectProgram = async (program) => {
    setSelectedProgram(program);
    setLoading(true);
    setError(null);

    try {
      const response = await fetchProgramFull(program.program_id);
      const data = response.data || response;
      setProgramData(data);
      setStep("program");
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setError(`Failed to load program: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSelector = () => {
    setError(null);
    setProgramData(null);
    setSelectedProgram(null);
    setSelectedSession(null);
    setStep("selector");
    // Refresh programs list
    fetchWorkoutPrograms()
      .then((response) => {
        const data = response.data || response;
        setProgramList(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
  };

  const handleLogout = () => {
    clearAuth();
    window.location.href = "/workout";
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: FONT }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 44, height: 44, border: "3px solid #f0f0f0", borderTopColor: "#111", borderRadius: "50%", animation: "spin 0.8s linear infinite", marginBottom: 18 }} />
        <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 }}>Loading your program</div>
        <div style={{ fontSize: 13, color: C.muted }}>Fetching workout data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: FONT }}>
        <div style={{ textAlign: "center", maxWidth: 360, background: "#fff", borderRadius: 20, padding: "48px 32px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: "#fee2e2", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="1.5"/>
              <path d="M12 8V12M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#111", marginBottom: 8 }}>Unable to Load Program</div>
          <div style={{ fontSize: 14, color: "#888", lineHeight: 1.5, marginBottom: 24 }}>{error}</div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button onClick={() => selectedProgram && handleSelectProgram(selectedProgram)} style={{
              padding: "12px 28px", borderRadius: 10, border: "none",
              background: "#111", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
            }}>Try Again</button>
            {programList.length > 1 && (
              <button onClick={handleBackToSelector} style={{
                padding: "12px 28px", borderRadius: 10, border: `1px solid ${C.borderLight}`,
                background: "#fff", color: C.text, fontSize: 14, fontWeight: 600, cursor: "pointer",
              }}>Back</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (step === "selector" || (!programData && programList.length > 1)) {
    if (programList.length === 0) {
      return (
        <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: FONT }}>
          <div style={{ textAlign: "center", maxWidth: 360, background: "#fff", borderRadius: 20, padding: "48px 32px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: "#f5f5f5", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M4 12H8M16 12H20M12 4V8M12 16V20" stroke="#999" strokeWidth="1.5" strokeLinecap="round"/>
                <rect x="8" y="8" width="8" height="8" rx="1" stroke="#999" strokeWidth="1.5"/>
              </svg>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#111", marginBottom: 8 }}>No Programs Available</div>
            <div style={{ fontSize: 14, color: "#888", lineHeight: 1.5 }}>You don't have any active workout programs assigned yet. Please check back later.</div>
          </div>
        </div>
      );
    }

    return (
      <ProgramSelectorStep
        programs={programList}
        onSelectProgram={handleSelectProgram}
        onLogout={handleLogout}
      />
    );
  }

  const getMetric = (metrics, type) => {
    const metric = metrics?.find((m) => m.metric_type === type);
    return metric?.metric_value_num || metric?.metric_value_text || null;
  };

  /**
   * Build structured exercise list from V2 nested blocks.
   * Groups (superset/circuit/cool_down with children) are preserved as group objects
   * so WorkoutStepAuth can render the round-based UI.
   * Uses `instance_id` as the key for set logging (per enduserui.md spec).
   */
  const flattenBlocks = (blocks) => {
    const exercises = [];
    let idx = 0;

    (blocks || []).forEach((block) => {
      const isContainer = ["superset", "circuit", "cool_down", "giant_set", "amrap", "emom", "warm_up"].includes(block.block_type) && block.children?.length > 0;

      if (isContainer) {
        const isAmrap = block.block_type === "amrap";
        // Preserve as a group with children
        const children = block.children.map((child) => {
          idx++;
          return {
            id: child.exercise_id || idx,
            instanceId: child.instance_id || null,
            blockId: child.instance_id || child.block_id || child.exercise_id || `${block.block_order}-${child.block_order}`,
            name: child.exercise_name || "Unnamed Exercise",
            // For grouped children: ignore child's "sets" metric — rounds come from parent
            sets: null,
            reps: String(getMetric(child.metrics, "reps") || 0),
            weight: String(getMetric(child.metrics, "weight") || getMetric(child.metrics, "load") || 0),
            weightUnit: child.metrics?.find((m) => m.metric_type === "weight" || m.metric_type === "load")?.unit || "lbs",
            tempo: null,
            rest: parseInt(getMetric(child.metrics, "rest")) || 0,
            videoUrl: child.video_url || null,
            notes: child.notes || null,
            status: child.status || "active",
            metrics: child.metrics || [],
            groupLabel: child.group_label || null,
            roundNo: block.round_no || null,
            isAmrap: isAmrap,
          };
        });

        exercises.push({
          _isGroup: true,
          groupType: block.block_type,
          groupLabel: block.group_label || null,
          roundNo: block.round_no || null,
          restSeconds: block.rest_seconds || 0,
          timeCap: block.time_cap_seconds || null,
          notes: block.notes || null,
          instanceId: block.instance_id || null,
          children: children,
        });
      } else if (block.exercise_name || block.block_type === "exercise") {
        // Standalone exercise
        idx++;
        exercises.push({
          id: block.exercise_id || block.block_id || idx,
          instanceId: block.instance_id || null,
          blockId: block.instance_id || block.block_id || block.exercise_id || `block-${block.block_order}`,
          name: block.exercise_name || "Unnamed Exercise",
          sets: parseInt(getMetric(block.metrics, "sets")) || 1,
          reps: String(getMetric(block.metrics, "reps") || 0),
          weight: String(getMetric(block.metrics, "weight") || getMetric(block.metrics, "load") || 0),
          weightUnit: block.metrics?.find((m) => m.metric_type === "weight" || m.metric_type === "load")?.unit || "lbs",
          tempo: null,
          rest: parseInt(getMetric(block.metrics, "rest")) || block.rest_seconds || 0,
          videoUrl: block.video_url || null,
          notes: block.notes || null,
          status: block.status || "active",
          metrics: block.metrics || [],
          groupLabel: null,
          groupType: null,
          parentGroupLabel: null,
          roundNo: null,
        });
      }
    });

    return exercises;
  };

  const workoutData = selectedSession && programData ? (() => {
    const sessionData = selectedSession.sessionData || programData.sessions?.find((s) => s.session_id === selectedSession.sessionId || s.id === selectedSession.sessionId);
    if (!sessionData) return null;

    const exercises = flattenBlocks(sessionData.blocks);

    return {
      sessionId: sessionData.session_id || sessionData.id,
      sessionStatus: sessionData.status || "scheduled",
      coachName: programData.program?.coach_name || programData.coach_name || "Your Coach",
      programName: programData.program?.name || programData.program_name || "Training Program",
      weekNumber: Math.ceil((selectedSession.day || 1) / 5),
      dayLabel: sessionData.session_name || sessionData.label || selectedSession.label || "Workout",
      date: selectedSession.date || "Today",
      coachNote: sessionData.notes || programData.program?.programming_guidance || null,
      exercises: exercises,
      coachSettings: { showTempo: false, showRest: true },
    };
  })() : null;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: FONT, maxWidth: 480, margin: "0 auto", position: "relative" }}>
      <style>{`
        @keyframes fadeInJourney { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideInRight { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
      `}</style>

      {step === "program" && programList.length > 1 && (
        <div style={{
          position: "sticky", top: 0, zIndex: 50, background: C.bg, padding: "14px 16px 10px",
          borderBottom: `1px solid ${C.borderLight}`, display: "flex", alignItems: "center", gap: 8,
        }}>
          <button onClick={handleBackToSelector} style={{
            background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
            color: C.sub, fontSize: 13, fontWeight: 600, fontFamily: FONT, padding: "4px 6px", borderRadius: 6,
          }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            All Programs
          </button>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: C.muted }}>
            <span style={{ width: 5, height: 5, borderRadius: 3, background: C.done }} />
            {selectedProgram?.name || "Program"}
          </div>
        </div>
      )}

      {step === "workout" && (
        <div style={{
          position: "sticky", top: 0, zIndex: 50, background: C.bg, padding: "14px 16px 10px",
          borderBottom: `1px solid ${C.borderLight}`, display: "flex", alignItems: "center", gap: 8,
        }}>
          <button onClick={() => setStep("program")} style={{
            background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
            color: C.sub, fontSize: 13, fontWeight: 600, fontFamily: FONT, padding: "4px 6px", borderRadius: 6,
          }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back to Program
          </button>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: C.muted }}>
            <span style={{ width: 5, height: 5, borderRadius: 3, background: C.done }} />
            {programData?.program_name || "Training Program"}
          </div>
        </div>
      )}

      <div key={step} style={{ animation: "slideInRight 0.35s ease" }}>
        {step === "program" && (
          <ProgramStepWrapper
            programData={programData}
            onWorkoutTap={(session) => {
              setSelectedSession(session);
              setStep("workout");
            }}
            onBack={programList.length > 1 ? handleBackToSelector : handleLogout}
          />
        )}
        {step === "workout" && <WorkoutStepAuth workoutData={workoutData} />}
      </div>
    </div>
  );
}
